import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { 
  Mail, Phone, MapPin, ArrowLeft, Send, 
  Building2, Clock, CheckCircle2, User, Briefcase, Loader2
} from "lucide-react";
import vexisLogo from "@assets/vexis-logo-2026.jpg";

export default function Contact() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [userType, setUserType] = useState<"company" | "candidate" | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });

  useEffect(() => {
    document.title = "Contact Vexis | Get in Touch";
  }, []);

  const agentEmail = "sales@vexistalentagency.com";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userType) {
      toast({
        title: "Please select who you are",
        description: "Let us know if you're a recruiter or candidate so we can assist you better.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/contact/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userType,
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          message: formData.message,
        }),
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast({
          title: "Message sent",
          description: "Your enquiry has been received. Our team will contact you shortly.",
        });
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      console.error("Submit error:", error);
      const userTypeLabel = userType === "company" ? "Recruiter/Hiring Team" : "Candidate";
      const mailtoLink = `mailto:${agentEmail}?subject=${encodeURIComponent(
        `${userTypeLabel} Enquiry from ${formData.company || formData.name}`
      )}&body=${encodeURIComponent(
        `Enquiry Type: ${userTypeLabel}\n\nName: ${formData.name}\nEmail: ${formData.email}\n${userType === "company" ? `Company: ${formData.company}\n` : ""}Phone: ${formData.phone}\n\nMessage:\n${formData.message}`
      )}`;
      window.location.href = mailtoLink;
      toast({
        title: "Email client opened",
        description: "Please send the email from your email client to complete your request.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#05070a", color: "#ffffff" }}>
      <nav className="sticky top-0 z-50" style={{ backgroundColor: "rgba(5,7,10,0.92)", borderBottom: "1px solid rgba(99,102,241,0.15)", backdropFilter: "blur(20px)" }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link href="/" className="flex items-center gap-3">
            <img src={vexisLogo} alt="VEXIS Talent Agency" className="h-10 md:h-12 w-auto" />
            <span className="text-white font-bold tracking-widest text-sm">VEXIS</span>
          </Link>
          <Link href="/">
            <Button variant="ghost" className="gap-2 font-medium text-slate-300 hover:text-white hover:bg-white/10" data-testid="button-back-home">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white">Submit Enquiry</h1>
            <p className="text-lg md:text-xl max-w-2xl mx-auto" style={{ color: "#94a3b8" }}>
              Get in touch with our team to discover how Vexis can streamline your assessment process.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <Card className="border-2 bg-slate-900 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-white">
                    <Mail className="w-6 h-6 text-blue-400" />
                    Contact Our Team
                  </CardTitle>
                  <CardDescription className="text-white/70">
                    Reach out directly to our hiring operations team
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-white border-b border-white/20 pb-2">Department Contacts</h4>
                    
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <a href="tel:+64212458827" className="text-blue-400 hover:underline font-medium" data-testid="link-phone">
                            +64 212 458 827
                          </a>
                          <p className="text-sm text-white/80">General enquiries and new business</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <a href="mailto:sales@vexistalentagency.com" className="text-blue-400 hover:underline font-medium" data-testid="link-sales-email">
                            sales@vexistalentagency.com
                          </a>
                          <p className="text-sm text-white/80">New business enquiries, pricing, and platform subscriptions</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <a href="mailto:privacy@vexistalentagency.com" className="text-blue-400 hover:underline font-medium" data-testid="link-privacy-email">
                            privacy@vexistalentagency.com
                          </a>
                          <p className="text-sm text-white/80">Data protection requests, privacy concerns, and GDPR/NZ Privacy Act enquiries</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <a href="mailto:administration@vexistalentagency.com" className="text-blue-400 hover:underline font-medium" data-testid="link-admin-email">
                            administration@vexistalentagency.com
                          </a>
                          <p className="text-sm text-white/80">Contracts, billing, invoicing, and account management</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <a href="mailto:tech_support@vexistalentagency.com" className="text-blue-400 hover:underline font-medium" data-testid="link-tech-email">
                            tech_support@vexistalentagency.com
                          </a>
                          <p className="text-sm text-white/80">Technical issues, platform access, and assessment system support</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <a href="mailto:operations@vexistalentagency.com" className="text-blue-400 hover:underline font-medium" data-testid="link-ops-email">
                            operations@vexistalentagency.com
                          </a>
                          <p className="text-sm text-white/80">Platform support, candidate pipeline queries, and assessment operations</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-white/20 pt-4 space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Response Time</p>
                        <p className="text-sm text-white/80">Within 24 business hours</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Global Coverage</p>
                        <p className="text-sm text-white/80">Europe, USA, Canada, Australia, New Zealand</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 text-white border-0">
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-xl font-semibold">Why Choose VEXIS?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span>Structured evaluation saves hours of manual review</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span>Detect AI-written resumes and verify authentic experience</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span>Decision-ready dossiers written back to your ATS automatically</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span>Every candidate outcome reviewed by a human agent</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span>Zero-bias AI scoring for fair evaluations</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <span>Reduce time-to-hire by up to 70%</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="rounded-2xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(99,102,241,0.18)" }}>
              <div className="p-6 pb-4" style={{ borderBottom: "1px solid rgba(99,102,241,0.12)" }}>
                <h3 className="text-xl font-semibold text-white">Send Us a Message</h3>
                <p className="text-sm mt-1" style={{ color: "#94a3b8" }}>Fill out the form below and we'll get back to you promptly</p>
              </div>
              <div className="p-6">
                {submitted ? (
                  <div className="text-center py-12 space-y-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: "rgba(34,197,94,0.15)" }}>
                      <CheckCircle2 className="w-8 h-8 text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white">Enquiry Received</h3>
                    <p style={{ color: "#94a3b8" }}>
                      Thank you for your enquiry. Our team will review your submission and contact you within 24 business hours.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setSubmitted(false)}
                      className="mt-4 border-white/20 text-white hover:bg-white/10"
                      data-testid="button-send-another"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-3">
                      <Label className="text-white">I am a... *</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setUserType("company")}
                          className="p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2"
                          style={{
                            borderColor: userType === "company" ? "#6366f1" : "rgba(255,255,255,0.1)",
                            backgroundColor: userType === "company" ? "rgba(99,102,241,0.1)" : "transparent",
                            color: userType === "company" ? "#a5b4fc" : "#94a3b8",
                          }}
                          data-testid="button-user-type-company"
                        >
                          <Building2 className="w-8 h-8" />
                          <span className="font-medium">Recruiter / Hiring Team</span>
                          <span className="text-xs opacity-70">Connect your ATS and get started</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setUserType("candidate")}
                          className="p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2"
                          style={{
                            borderColor: userType === "candidate" ? "#6366f1" : "rgba(255,255,255,0.1)",
                            backgroundColor: userType === "candidate" ? "rgba(99,102,241,0.1)" : "transparent",
                            color: userType === "candidate" ? "#a5b4fc" : "#94a3b8",
                          }}
                          data-testid="button-user-type-candidate"
                        >
                          <User className="w-8 h-8" />
                          <span className="font-medium">Candidate</span>
                          <span className="text-xs opacity-70">Assessment support</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-white text-sm">Full Name *</Label>
                        <Input id="name" required value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="John Smith" data-testid="input-name"
                          className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-white text-sm">Email *</Label>
                        <Input id="email" type="email" required value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="john@company.com" data-testid="input-email"
                          className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500" />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      {userType === "company" && (
                        <div className="space-y-2">
                          <Label htmlFor="company" className="text-white text-sm">Company *</Label>
                          <Input id="company" required={userType === "company"} value={formData.company}
                            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                            placeholder="Company Name" data-testid="input-company"
                            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500" />
                        </div>
                      )}
                      <div className={`space-y-2 ${userType !== "company" ? "sm:col-span-2" : ""}`}>
                        <Label htmlFor="phone" className="text-white text-sm">Phone</Label>
                        <Input id="phone" type="tel" value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+1 (555) 000-0000" data-testid="input-phone"
                          className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-white text-sm">Message *</Label>
                      <Textarea id="message" required rows={5} value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder={
                          userType === "candidate"
                            ? "Tell us about your assessment experience or the support you need."
                            : "Tell us about your recruitment workflow and what you're looking to improve."
                        }
                        data-testid="input-message"
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500" />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-12 text-lg"
                      disabled={isSubmitting}
                      data-testid="button-submit-contact"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>

                    <p className="text-sm text-center" style={{ color: "#94a3b8" }}>
                      We'll get back to you within 24 business hours.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
